import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import Shield from 'lucide-react/dist/esm/icons/shield';
import Users from 'lucide-react/dist/esm/icons/users';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign';
import Heart from 'lucide-react/dist/esm/icons/heart';
import Brain from 'lucide-react/dist/esm/icons/brain';
import PawPrint from 'lucide-react/dist/esm/icons/paw-print';
import LifeBuoy from 'lucide-react/dist/esm/icons/life-buoy';
import BookOpen from 'lucide-react/dist/esm/icons/book-open';
import Plane from 'lucide-react/dist/esm/icons/plane';
import Calendar from 'lucide-react/dist/esm/icons/calendar';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import Clock from 'lucide-react/dist/esm/icons/clock';
import Target from 'lucide-react/dist/esm/icons/target';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { safeLocalStorage } from '../../lib/utils';

interface Step {
  question: string;
  description?: string;
  type: 'single' | 'multiple' | 'explainer' | 'module-selection';
  options?: string[];
  maxSelections?: number;
  points?: Record<string, number>;
  dependsOn?: {
    question: number;
    answers: string[];
  };
}

interface Module {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  comingSoon?: boolean;
}

const modules: Module[] = [
  {
    id: 'digital',
    name: 'Digital Estate',
    description: 'Secure password vault, device management, and digital asset organisation',
    icon: <Shield size={24} />,
    color: 'bg-idecide-primary'
  },
  {
    id: 'legal',
    name: 'Legal Estate',
    description: 'Wills, trusts, power of attorney, and advance care directives',
    icon: <FileText size={24} />,
    color: 'bg-idecide-secondary'
  },
  {
    id: 'financial',
    name: 'Financial Management',
    description: 'Bank accounts, investments, insurance, and retirement planning',
    icon: <DollarSign size={24} />,
    color: 'bg-idecide-success'
  },
  {
    id: 'health',
    name: 'Health Management',
    description: 'Medical records, appointments, medications, and emergency contacts',
    icon: <Heart size={24} />,
    color: 'bg-idecide-error'
  },
  {
    id: 'wellbeing',
    name: 'Wellbeing',
    description: 'Mental health resources, counselling, and wellness planning',
    icon: <Brain size={24} />,
    color: 'bg-idecide-info'
  },
  {
    id: 'pets',
    name: 'Pets Management',
    description: 'Pet profiles, care schedules, medical records, and veterinary info',
    icon: <PawPrint size={24} />,
    color: 'bg-idecide-warning'
  },
  {
    id: 'ndis',
    name: 'NDIS Management',
    description: 'NDIS plan management, service providers, and budget tracking',
    icon: <LifeBuoy size={24} />,
    color: 'bg-idecide-primary'
  },
  {
    id: 'legacy',
    name: 'Legacy Management',
    description: 'Memory books, letters, videos, and legacy documentation',
    icon: <BookOpen size={24} />,
    color: 'bg-idecide-secondary'
  },
  {
    id: 'travel',
    name: 'Travel Management',
    description: 'Trip planning, documents, accommodation, and itineraries',
    icon: <Plane size={24} />,
    color: 'bg-idecide-accent'
  },
  {
    id: 'emergency',
    name: 'Emergency Management',
    description: 'Emergency plans, contacts, resources, and quick action kits',
    icon: <AlertTriangle size={24} />,
    color: 'bg-red-500'
  }
];

const steps: Step[] = [
  {
    question: "Welcome to iDecide",
    description: "Your comprehensive life management platform",
    type: "explainer"
  },
  {
    question: "Do you have a partner?",
    type: "single",
    options: ["Yes", "No"],
    points: {
      "Yes": 20,
      "No": 10
    }
  },
  {
    question: "Does your partner know where to find important documents?",
    type: "single",
    options: ["Yes, they know exactly where", "They have a general idea", "No, they don't know"],
    dependsOn: {
      question: 1,
      answers: ["Yes"]
    },
    points: {
      "Yes, they know exactly where": 50,
      "They have a general idea": 25,
      "No, they don't know": 0
    }
  },
  {
    question: "Do you have children? If so, what age groups?",
    type: "multiple",
    options: ["No children", "0-5 years", "6-12 years", "13-17 years", "18+ years"],
    maxSelections: 4,
    points: {
      "No children": 10,
      "0-5 years": 15,
      "6-12 years": 15,
      "13-17 years": 15,
      "18+ years": 15
    }
  },
  {
    question: "Do you have any dependents other than children?",
    type: "multiple",
    options: ["Elderly parents", "Disabled family member", "Other dependents", "No other dependents"],
    maxSelections: 3,
    points: {
      "Elderly parents": 20,
      "Disabled family member": 20,
      "Other dependents": 15,
      "No other dependents": 10
    }
  },
  {
    question: "What is your current housing situation?",
    type: "single",
    options: ["Own", "Rent", "Other"],
    points: {
      "Own": 30,
      "Rent": 20,
      "Other": 10
    }
  },
  {
    question: "How quickly can you access your property deed or rental agreement?",
    type: "single",
    options: ["Very fast - I know exactly where it is", "Takes some searching", "No idea where it is"],
    points: {
      "Very fast - I know exactly where it is": 40,
      "Takes some searching": 20,
      "No idea where it is": 0
    }
  },
  {
    question: "Which assets do you own?",
    type: "multiple",
    options: ["Car", "Other vehicle", "Small business", "Other real estate", "None of these"],
    maxSelections: 4,
    points: {
      "Car": 15,
      "Other vehicle": 15,
      "Small business": 25,
      "Other real estate": 25,
      "None of these": 5
    }
  },
  {
    question: "What types of insurance do you have?",
    type: "multiple",
    options: [
      "Health insurance",
      "Car insurance",
      "Property insurance",
      "Life insurance (through employer)",
      "Life insurance (standalone)",
      "Disability insurance",
      "Long-term care insurance",
      "None of these"
    ],
    maxSelections: 7,
    points: {
      "Health insurance": 15,
      "Car insurance": 15,
      "Property insurance": 15,
      "Life insurance (through employer)": 20,
      "Life insurance (standalone)": 25,
      "Disability insurance": 20,
      "Long-term care insurance": 20,
      "None of these": 0
    }
  },
  {
    question: "Does your family know about all your insurance policies?",
    type: "single",
    options: ["Yes, they know about all of them", "They know about some", "No, they don't know"],
    points: {
      "Yes, they know about all of them": 40,
      "They know about some": 20,
      "No, they don't know": 0
    }
  },
  {
    question: "Which legal documents do you have?",
    type: "multiple",
    options: ["Will", "Power of Attorney", "Advance Care Directive", "Trust", "Other", "None of the above"],
    maxSelections: 5,
    points: {
      "Will": 30,
      "Power of Attorney": 30,
      "Advance Care Directive": 30,
      "Trust": 30,
      "Other": 15,
      "None of the above": 0
    }
  },
  {
    question: "Does someone you trust know where you keep the physical documents?",
    type: "single",
    options: ["All", "Some", "None"],
    points: {
      "All": 40,
      "Some": 20,
      "None": 0
    }
  },
  {
    question: "What do you hope to get out of iDecide?",
    type: "multiple",
    options: [
      "Organise critical info, docs, and IDs",
      "Organise info of those in my care",
      "Make sure I have the right legal docs",
      "Make sure I have the right insurance policies",
      "Have a plan in case something happens to me",
      "A secure online vault with sharing capabilities",
      "Document my legacy and final wishes",
      "Unsure / Just exploring"
    ],
    maxSelections: 3,
    points: {
      "Organise critical info, docs, and IDs": 15,
      "Organise info of those in my care": 15,
      "Make sure I have the right legal docs": 15,
      "Make sure I have the right insurance policies": 15,
      "Have a plan in case something happens to me": 15,
      "A secure online vault with sharing capabilities": 15,
      "Document my legacy and final wishes": 15,
      "Unsure / Just exploring": 5
    }
  },
  {
    question: "Choose Your Modules",
    description: "Select the modules you'd like to use in iDecide",
    type: "module-selection"
  }
];

export function OnboardingFlow() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string | string[]>>({});
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const { refreshProfile } = useAuth();

  // Initialize onboarding
  useEffect(() => {
    const initializeOnboarding = async () => {
      try {
        console.log('Initializing onboarding...');
        // Any initialization logic can go here
      } catch (err) {
        console.error('Error initializing onboarding:', err);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeOnboarding();
  }, []);

  // Calculate which steps are actually visible based on dependencies
  const getVisibleSteps = () => {
    const visibleSteps: number[] = [];
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      if (!step.dependsOn || 
          (answers[step.dependsOn.question] && 
           step.dependsOn.answers.includes(answers[step.dependsOn.question] as string))) {
        visibleSteps.push(i);
      }
    }
    return visibleSteps;
  };

  // When the answer to a dependsOn question changes, reset downstream answers
  useEffect(() => {
    // If the answer to 'Do you have a partner?' changes to 'No', clear answer to Q2
    if (answers[1] === 'No' && answers[2]) {
      setAnswers(prev => {
        const newAnswers = { ...prev };
        delete newAnswers[2];
        return newAnswers;
      });
    }
  }, [answers[1]]);

  // Skip dependent questions that don't apply — runs in effect to avoid state updates during render
  useEffect(() => {
    const question = steps[currentStep];
    if (!question?.dependsOn) return;
    const dependentAnswer = answers[question.dependsOn.question];
    if (!question.dependsOn.answers.includes(dependentAnswer as string)) {
      let next = currentStep + 1;
      while (next < steps.length) {
        const s = steps[next];
        if (!s.dependsOn || s.dependsOn.answers.includes(answers[s.dependsOn.question] as string)) break;
        next++;
      }
      // If nothing valid is left ahead, fall back to the last step (module selection,
      // which has no dependencies) so we never render `null` forever.
      setCurrentStep(next < steps.length ? next : steps.length - 1);
      setSelectedOptions([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Restore selected modules when navigating to module selection step
  useEffect(() => {
    const currentQuestion = steps[currentStep];
    if (currentQuestion?.type === 'module-selection') {
      const savedModules = answers[currentStep];
      if (savedModules && Array.isArray(savedModules) && savedModules.length > 0) {
        setSelectedModules(savedModules as string[]);
      }
    }
  }, [currentStep]);

  // Get the actual step number (1-based) for display
  const getDisplayStepNumber = (stepIndex: number) => {
    const visibleSteps = getVisibleSteps();
    const visibleIndex = visibleSteps.indexOf(stepIndex);
    return visibleIndex + 1;
  };

  // Get total number of visible steps
  const getTotalVisibleSteps = () => {
    return getVisibleSteps().length;
  };

  const calculateScore = (allAnswers: Record<number, string | string[]>): number => {
    let totalScore = 0;
    steps.forEach((step, idx) => {
      const answer = allAnswers[idx];
      if (step?.points && answer !== undefined) {
        if (Array.isArray(answer)) {
          answer.forEach(ans => {
            totalScore += step.points?.[ans] || 0;
          });
        } else {
          totalScore += step.points?.[answer as string] || 0;
        }
      }
    });
    return Math.min(totalScore, 500);
  };

  const completeOnboarding = async (finalAnswers?: Record<number, string | string[]>) => {
    if (isCompleting) return;
    setIsCompleting(true);

    const answersToUse = finalAnswers ?? answers;

    // Add a timeout to prevent getting stuck
    const timeoutId = setTimeout(() => {
      console.error('Onboarding completion timed out');
      setIsCompleting(false);
      toast.error('The setup is taking longer than expected. Please try again.');
    }, 30000); // 30 second timeout

    try {
      console.log('Starting onboarding completion...');
      const finalScore = calculateScore(answersToUse);
      console.log('Final score calculated:', finalScore);
      console.log('All answers:', answersToUse);
      
      // Save answers and score to Supabase
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error('Error getting user:', userError);
        throw userError;
      }
      console.log('User retrieved:', user?.id);
      
      if (user) {
        // Build sidebar items based on selected modules so the user dashboard only shows what they picked
        const defaultSidebarItems = [
          { id: 'dashboard', label: 'Dashboard', iconName: 'dashboard', section: 'dashboard', visible: true },
          { id: 'digital', label: 'Digital', iconName: 'digital', section: 'digital', visible: true },
          { id: 'legal', label: 'Legal', iconName: 'legal', section: 'legal', visible: true },
          { id: 'financial', label: 'Financial', iconName: 'financial', section: 'financial', visible: true },
          { id: 'budgeting', label: 'Budgeting', iconName: 'budgeting', section: 'budgeting', visible: true },
          { id: 'health', label: 'Health', iconName: 'health', section: 'health', visible: true },
          { id: 'wellbeing', label: 'Wellbeing', iconName: 'wellbeing', section: 'wellbeing', visible: true },
          { id: 'pets', label: 'Pets', iconName: 'pets', section: 'pets', visible: true },
          { id: 'ndis', label: 'NDIS', iconName: 'ndis', section: 'ndis', visible: true },
          { id: 'legacy', label: 'Legacy', iconName: 'legacy', section: 'legacy', visible: true },
          { id: 'travel', label: 'Travel', iconName: 'travel', section: 'travel', visible: true },
          { id: 'emergency', label: 'Emergency', iconName: 'emergency', section: 'emergency', visible: true },
          { id: 'calendar', label: 'Calendar', iconName: 'calendar', section: 'calendar', visible: false }
        ];

        // Toggle visibility based on selected modules
        const sidebarItems = defaultSidebarItems.map(item => {
          // Always keep dashboard visible
          if (item.id === 'dashboard') return item;
          return { ...item, visible: selectedModules.includes(item.id) };
        });

        // Save onboarding data to user_preferences table
        const onboardingData = {
          user_id: user.id,
          onboarding_answers: {
            has_partner: answersToUse[1] === 'Yes',
            partner_document_access: answersToUse[2] as string,
            children_age_groups: Array.isArray(answersToUse[3]) ? answersToUse[3] : [],
            dependents: Array.isArray(answersToUse[4]) ? answersToUse[4] : [],
            housing_status: answersToUse[5] as string,
            property_deed_access: answersToUse[6] as string,
            assets: Array.isArray(answersToUse[7]) ? answersToUse[7] : [],
            insurance_types: Array.isArray(answersToUse[8]) ? answersToUse[8] : [],
            family_policy_awareness: answersToUse[9] as string,
            legal_documents: Array.isArray(answersToUse[10]) ? answersToUse[10] : [],
            document_location_shared: answersToUse[11] as string,
            goals: Array.isArray(answersToUse[12]) ? answersToUse[12] : [],
            selected_modules: selectedModules
          },
          sidebar_items: JSON.stringify(sidebarItems)
        };

        console.log('Saving onboarding data to user_preferences:', onboardingData);
        const { error: preferencesError } = await supabase
          .from('user_preferences')
          .upsert(onboardingData, { onConflict: 'user_id' });

        if (preferencesError) {
          console.error('Error saving user preferences:', preferencesError);
          throw preferencesError;
        }

        console.log('User preferences saved successfully');

        // Store sidebar configuration in localStorage for instant effect
        try {
          safeLocalStorage.setItem(`sidebar_config_${user.id}`, JSON.stringify(sidebarItems));
        } catch (e) {
          console.warn('Failed to save sidebar config to localStorage', e);
        }

        // Update profile directly in database to mark onboarding as completed
        try {
          const { error: profileError } = await supabase
            .from('profiles')
            .update({ 
              onboarding_completed: true,
              idecide_score: finalScore
            })
            .eq('id', user.id);
          
          if (profileError) {
            console.error('Error updating profile:', profileError);
            throw profileError;
          } else {
            console.log('Profile updated successfully');
          }
        } catch (profileErr) {
          console.error('Error updating profile:', profileErr);
          throw profileErr;
        }

        // CRITICAL FIX: Refresh profile in AuthContext so router sees updated onboarding_completed status
        console.log('Refreshing profile in AuthContext...');
        if (refreshProfile) {
          await refreshProfile();
          console.log('Profile refreshed in AuthContext');
        }

        console.log('Onboarding completed, redirecting to dashboard...');

        // Clear timeout since we're about to redirect
        clearTimeout(timeoutId);
        setIsCompleting(false);

        // Use window.location for hard navigation to ensure router sees updated profile
        // Small delay to ensure profile refresh completes
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 100);
      } else {
        throw new Error('No user found');
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
      clearTimeout(timeoutId);
      setIsCompleting(false);
      // Show error to user with more details
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`There was an error setting up your dashboard: ${errorMessage}. Please try again.`, {
        duration: 8000,
      });
    }
  };

  const handleNext = async () => {
    console.log('🔄 handleNext called, currentStep:', currentStep, 'isCompleting:', isCompleting);
    
    // Prevent multiple clicks while processing
    if (isCompleting) {
      console.log('⏸️ Onboarding already completing, ignoring click');
      return;
    }
    
    if (currentStep === 0) {
      // Explainer step - just move to next
      console.log('➡️ Moving from explainer to step 1');
      setCurrentStep(1);
      return;
    }

    if (currentQuestion.type === 'module-selection') {
      // Validate that at least one module is selected
      if (selectedModules.length === 0) {
        toast.error('Please select at least one module to continue.');
        return;
      }
      console.log('✅ Module selection complete, finishing onboarding with modules:', selectedModules);
      const newAnswers = { ...answers, [currentStep]: selectedModules };
      setAnswers(newAnswers);
      // Finish onboarding with the up-to-date answers (avoids reading stale state)
      await completeOnboarding(newAnswers);
      return;
    }

    const newAnswers = { ...answers, [currentStep]: currentQuestion.type === 'single' ? selectedOptions[0] : selectedOptions };
    setAnswers(newAnswers);
    console.log('💾 Saved answer for step', currentStep, ':', newAnswers[currentStep]);

    if (currentStep === steps.length - 1) {
      // Final step - complete onboarding
      console.log('🏁 Final step reached, completing onboarding');
      await completeOnboarding(newAnswers);
    } else {
      // Find next applicable step. This must read `newAnswers`, not `answers` —
      // React state updates are async, so `answers` still holds the value from
      // before this click and dependent questions would be wrongly skipped.
      let nextStep = currentStep + 1;
      while (nextStep < steps.length) {
        const step = steps[nextStep];
        if (!step.dependsOn ||
            (newAnswers[step.dependsOn.question] &&
             step.dependsOn.answers.includes(newAnswers[step.dependsOn.question] as string))) {
          break;
        }
        nextStep++;
      }
      console.log('➡️ Moving to next step:', nextStep);
      setCurrentStep(nextStep);

      // Restore any previously given answer for the step we're moving to
      const nextStepQuestion = steps[nextStep];
      const savedAnswer = newAnswers[nextStep];

      if (nextStepQuestion?.type === 'module-selection') {
        setSelectedOptions([]);
        setSelectedModules(Array.isArray(savedAnswer) ? savedAnswer as string[] : []);
      } else {
        setSelectedModules([]);
        setSelectedOptions(
          Array.isArray(savedAnswer)
            ? savedAnswer as string[]
            : savedAnswer
              ? [savedAnswer as string]
              : []
        );
      }
    }
  };

  const handleBack = () => {
    let prevStep = currentStep - 1;
    while (prevStep >= 0) {
      const step = steps[prevStep];
      if (!step.dependsOn || 
          (answers[step.dependsOn.question] && 
           step.dependsOn.answers.includes(answers[step.dependsOn.question] as string))) {
        break;
      }
      prevStep--;
    }
    setCurrentStep(prevStep);
    
    // Restore selected options for regular questions
    setSelectedOptions(
      Array.isArray(answers[prevStep]) 
        ? answers[prevStep] as string[] 
        : answers[prevStep] 
          ? [answers[prevStep] as string] 
          : []
    );
    
    // Restore selected modules if going back to module selection step
    const prevStepQuestion = steps[prevStep];
    if (prevStepQuestion?.type === 'module-selection') {
      const prevModules = answers[prevStep];
      setSelectedModules(
        Array.isArray(prevModules) ? prevModules as string[] : []
      );
    } else {
      // Clear selected modules when not on module selection step
      setSelectedModules([]);
    }
  };


  const currentQuestion = steps[currentStep];

  // Guard: if we somehow land on a dependent step that shouldn't be visible, render nothing —
  // the useEffect above will advance to the next valid step.
  if (currentQuestion?.dependsOn) {
    const dependentAnswer = answers[currentQuestion.dependsOn.question];
    if (!currentQuestion.dependsOn.answers.includes(dependentAnswer as string)) {
      return null;
    }
  }

  const handleOptionClick = (option: string) => {
    if (currentQuestion.type === 'single') {
      setSelectedOptions([option]);
    } else {
      setSelectedOptions(prev => {
        if (prev.includes(option)) {
          return prev.filter(o => o !== option);
        } else {
          if (currentQuestion.maxSelections && prev.length >= currentQuestion.maxSelections) {
            return prev;
          }
          return [...prev, option];
        }
      });
    }
  };

  const handleModuleToggle = (moduleId: string) => {
    setSelectedModules(prev => {
      if (prev.includes(moduleId)) {
        return prev.filter(id => id !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };

  const isOptionSelected = (option: string) => selectedOptions.includes(option);
  const isModuleSelected = (moduleId: string) => selectedModules.includes(moduleId);
  
  const canContinue = currentQuestion.type === 'explainer' || 
                     (currentQuestion.type === 'module-selection' && selectedModules.length > 0) ||
                     (currentQuestion.type !== 'module-selection' && selectedOptions.length > 0);

  // Show loading screen while initializing
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-idecide-primary mx-auto mb-4"></div>
          <p className="text-gray-600">
            Setting up your onboarding experience...
          </p>
        </div>
      </div>
    );
  }


  // Render explainer step
  if (currentQuestion.type === 'explainer') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-idecide-light to-idecide-primary flex items-center justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-4xl w-full mx-4 transform transition-all duration-500 ease-out">
          <div className="text-center mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-idecide-primary to-idecide-accent rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield size={48} className="text-white" />
            </div>
            <h1 className="text-4xl font-bold text-idecide-dark mb-4">Welcome to iDecide</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-6">
              Your comprehensive life management platform that helps you organise, secure, and share what matters most.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-6 rounded-xl bg-idecide-light">
              <Shield size={32} className="text-idecide-primary mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-idecide-dark">Secure & Private</h3>
              <p className="text-gray-600">Bank-level encryption keeps your information safe and private</p>
            </div>
            <div className="text-center p-6 rounded-xl bg-idecide-light">
              <Users size={32} className="text-idecide-secondary mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-idecide-dark">Share with Trust</h3>
              <p className="text-gray-600">Control who has access to your information when it matters most</p>
            </div>
            <div className="text-center p-6 rounded-xl bg-idecide-light">
              <FileText size={32} className="text-idecide-accent mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-idecide-dark">Organise Everything</h3>
              <p className="text-gray-600">From legal documents to pet care, keep everything in one place</p>
            </div>
          </div>

          {/* New section explaining why we ask questions */}
          <div className="bg-gradient-to-r from-idecide-light to-idecide-primary/10 rounded-xl p-6 mb-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-idecide-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <Target size={28} className="text-white" />
              </div>
              <h3 className="text-xl font-semibold text-idecide-dark mb-4">Why We Ask Questions</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                We'll ask you a few quick questions to understand your unique situation and needs. This helps us:
              </p>
              <div className="grid md:grid-cols-2 gap-4 text-left">
                <div className="flex items-start">
                  <CheckCircle size={20} className="text-idecide-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-idecide-dark">Personalise Your Experience</h4>
                    <p className="text-sm text-gray-600">Show you the most relevant features and modules for your life</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle size={20} className="text-idecide-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-idecide-dark">Calculate Your iDecide Score</h4>
                    <p className="text-sm text-gray-600">Assess how prepared you are and identify areas to improve</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle size={20} className="text-idecide-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-idecide-dark">Provide Smart Recommendations</h4>
                    <p className="text-sm text-gray-600">Suggest next steps and important documents you might need</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle size={20} className="text-idecide-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-idecide-dark">Create Your Custom Dashboard</h4>
                    <p className="text-sm text-gray-600">Set up widgets and tools that matter most to your situation</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-white/50 rounded-lg">
                <p className="text-sm text-gray-600 flex items-center justify-center">
                  <Clock size={16} className="mr-2" />
                  This will only take 2-3 minutes and makes your iDecide experience much more valuable
                </p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={handleNext}
              className="bg-gradient-to-r from-idecide-primary to-idecide-accent text-white px-8 py-3 rounded-lg text-lg font-semibold hover:from-idecide-primary-hover hover:to-idecide-accent-hover transform hover:scale-105 transition-all duration-200 shadow-lg"
            >
              Let's Get Started
              <ChevronRight size={20} className="ml-2 inline" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render module selection step
  if (currentQuestion.type === 'module-selection') {
    return (
      <div className="min-h-screen bg-idecide-light/80 backdrop-blur-sm flex items-center justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-6xl w-full mx-4 transform transition-all duration-500 ease-out max-h-[90vh] overflow-y-auto">
          <div className="mb-8">
            <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
              <div 
                className="bg-gradient-to-r from-idecide-primary to-idecide-accent h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${(getDisplayStepNumber(currentStep) / getTotalVisibleSteps()) * 100}%` }}
              />
            </div>
            <div className="mt-2 text-sm text-gray-500 text-center">
              Step {getDisplayStepNumber(currentStep)} of {getTotalVisibleSteps()}
            </div>
          </div>

          <h2 className="text-3xl font-bold text-center mb-2 text-idecide-dark">
            {currentQuestion.question}
          </h2>
          <p className="text-gray-600 text-center mb-8 text-lg">
            {currentQuestion.description}
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {modules.map((module) => (
              <button
                key={module.id}
                onClick={() => !module.comingSoon && handleModuleToggle(module.id)}
                disabled={!!module.comingSoon}
                className={`w-full p-4 rounded-lg border-2 transition-all duration-300 transform hover:scale-105 flex items-center gap-3 mb-3
                  ${isModuleSelected(module.id) ? 'border-idecide-primary bg-idecide-light shadow-md' : 'border-gray-200 hover:border-idecide-primary hover:shadow-sm'}
                  ${module.comingSoon ? 'opacity-60 cursor-not-allowed' : ''}
                `}
              >
                <span className={`w-10 h-10 flex items-center justify-center rounded-lg ${module.color} text-white mr-3`}>{module.icon}</span>
                <span className="flex-1 text-left">
                  <span className="block text-idecide-dark font-medium">{module.name}</span>
                  <span className="block text-xs text-gray-500">{module.description}</span>
                </span>
                {module.comingSoon && (
                  <span className="ml-2 px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700 font-semibold">Coming Soon</span>
                )}
                {isModuleSelected(module.id) && !module.comingSoon && (
                  <CheckCircle size={18} className="text-idecide-primary ml-2" />
                )}
              </button>
            ))}
          </div>

          {selectedModules.length === 0 && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
              <p className="text-sm text-amber-700">
                Please select at least one module to continue
              </p>
            </div>
          )}

          <div className="flex justify-between items-center">
            <button
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-idecide-dark transition-colors"
            >
              <ChevronLeft size={20} />
              <span>Back</span>
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                console.log('🔘 Module selection continue button clicked, selectedModules:', selectedModules.length, 'isCompleting:', isCompleting);
                if (selectedModules.length > 0 && !isCompleting) {
                  handleNext();
                } else {
                  console.log('⚠️ Module selection button click ignored - selectedModules:', selectedModules.length, 'isCompleting:', isCompleting);
                }
              }}
              disabled={selectedModules.length === 0 || isCompleting}
              className={`flex items-center px-6 py-3 rounded-lg transition-all duration-200 transform ${
                selectedModules.length > 0 && !isCompleting
                  ? 'bg-gradient-to-r from-idecide-primary to-idecide-accent text-white hover:from-idecide-primary-hover hover:to-idecide-accent-hover hover:scale-105'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isCompleting ? (
                <span className="flex items-center gap-2">
                  <span className="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                  Processing...
                </span>
              ) : (
                <>
                  <span>Continue</span>
                  <ChevronRight size={20} className="ml-2" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-idecide-light/80 backdrop-blur-sm flex items-center justify-center p-4 py-8">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full mx-4 transform transition-all duration-500 ease-out max-h-[90vh] overflow-y-auto">
        <div className="mb-8">
          <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
            <div 
              className="bg-gradient-to-r from-idecide-primary to-idecide-accent h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${(getDisplayStepNumber(currentStep) / getTotalVisibleSteps()) * 100}%` }}
            />
          </div>
          <div className="mt-2 text-sm text-gray-500 text-center">
            Step {getDisplayStepNumber(currentStep)} of {getTotalVisibleSteps()}
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-center mb-4 text-idecide-dark">
          {currentQuestion.question}
        </h2>

        {currentQuestion.description && (
          <p className="text-gray-600 text-center mb-6">
            {currentQuestion.description}
          </p>
        )}

        <div className="space-y-3 mb-8">
          {currentQuestion.options?.map((option) => (
            <button
              key={option}
              onClick={() => handleOptionClick(option)}
              disabled={!!(currentQuestion.maxSelections && 
                selectedOptions.length >= currentQuestion.maxSelections && 
                !selectedOptions.includes(option))}
              className={`w-full p-4 rounded-lg border-2 transition-all duration-300 transform hover:scale-105
                ${isOptionSelected(option)
                  ? 'border-idecide-primary bg-idecide-light shadow-md' 
                  : 'border-gray-200 hover:border-idecide-primary hover:shadow-sm'
                }
                ${currentQuestion.maxSelections && 
                  selectedOptions.length >= currentQuestion.maxSelections && 
                  !selectedOptions.includes(option)
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
                }`}
            >
              <div className="flex items-center">
                <div className={`w-5 h-5 rounded-${currentQuestion.type === 'multiple' ? 'sm' : 'full'} border-2 mr-3 flex items-center justify-center
                  ${isOptionSelected(option)
                    ? 'border-idecide-primary bg-idecide-primary'
                    : 'border-gray-300 bg-white'
                  }`}
                >
                  {isOptionSelected(option) && (
                    <div className={`w-3 h-3 ${currentQuestion.type === 'multiple' ? 'bg-white' : 'rounded-full bg-white'}`} />
                  )}
                </div>
                <span className="text-idecide-dark">{option}</span>
              </div>
            </button>
          ))}
        </div>

        {currentQuestion.maxSelections && (
          <p className="text-sm text-gray-500 text-center mb-4">
            Selected {selectedOptions.length} of {currentQuestion.maxSelections} maximum
          </p>
        )}

        <div className="flex justify-between items-center">
          {currentStep > 0 ? (
            <button
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-idecide-dark transition-colors"
            >
              <ChevronLeft size={20} />
              <span>Back</span>
            </button>
          ) : (
            <div className="w-20" />
          )}

          <button
            onClick={(e) => {
              e.preventDefault();
              console.log('🔘 Continue button clicked, canContinue:', canContinue, 'isCompleting:', isCompleting);
              if (canContinue && !isCompleting) {
                handleNext();
              } else {
                console.log('⚠️ Button click ignored - canContinue:', canContinue, 'isCompleting:', isCompleting);
              }
            }}
            disabled={!canContinue || isCompleting}
            className={`flex items-center px-6 py-3 rounded-lg transition-all duration-200 transform hover:scale-105
              ${canContinue && !isCompleting
                ? 'bg-gradient-to-r from-idecide-primary to-idecide-accent text-white hover:from-idecide-primary-hover hover:to-idecide-accent-hover'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
          >
            {isCompleting ? (
              <span className="flex items-center gap-2">
                <span className="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                Processing...
              </span>
            ) : (
              <>
                <span>{currentStep === steps.length - 1 ? 'Finish' : 'Continue'}</span>
                <ChevronRight size={20} className="ml-2" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}